import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { SectionTitle, StatusPill } from "@/components/dashboard/widgets";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import WalletCard from "@/components/dashboard/WalletCard";
import DeliveryHoursCard from "@/components/dashboard/DeliveryHoursCard";
import { Download, Sparkles, Loader2, AlertTriangle, CheckCircle2, TrendingUp, Gauge, PauseCircle } from "lucide-react";
import { toast } from "sonner";

export default function Billing() {
  const [data, setData] = useState(null);
  const [sub, setSub] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [capDraft, setCapDraft] = useState("");
  const [savingCap, setSavingCap] = useState(false);
  const [busyPlan, setBusyPlan] = useState(null);
  const [paused, setPaused] = useState(false);
  const [pausing, setPausing] = useState(false);

  const refresh = () => {
    api.get("/client/billing").then(r => { setData(r.data); setPaused(!!r.data?.delivery_paused); });
    api.get("/payments/subscription").then(r => setSub(r.data)).catch(() => setSub({ status: "none", all_plans: [] }));
    api.get("/client/preferences").then(r => { setPrefs(r.data); setCapDraft(r.data?.daily_lead_cap ?? ""); }).catch(() => setPrefs({ daily_lead_cap: null, today_accepted: 0 }));
  };
  useEffect(() => { refresh(); }, []);

  const togglePause = async () => {
    setPausing(true);
    try {
      const { data: out } = await api.post("/client/pause-delivery", { paused: !paused });
      setPaused(out.delivery_paused);
      toast.success(out.delivery_paused ? "Lead delivery paused" : "Lead delivery resumed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setPausing(false);
    }
  };

  const subscribeTo = async (planId) => {
    setBusyPlan(planId);
    try {
      const { data: out } = await api.post("/payments/checkout/session", {
        package_id: planId,
        origin_url: window.location.origin,
      });
      window.location.href = out.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout — please try again");
      setBusyPlan(null);
    }
  };

  const saveCap = async () => {
    setSavingCap(true);
    try {
      const cap = capDraft === "" || capDraft === null ? 0 : Math.max(0, parseInt(capDraft, 10) || 0);
      await api.patch("/client/preferences", { daily_lead_cap: cap });
      toast.success(cap === 0 ? "Daily cap removed — leads will flow unlimited." : `Daily cap set to ${cap.toLocaleString()} leads.`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save cap — please try again");
    } finally {
      setSavingCap(false);
    }
  };

  if (!data) return <div className="text-sm text-slate-500">Loading billing…</div>;
  const { invoices } = data;
  const needsRenewal = sub?.needs_renewal && sub?.status !== "none";
  const noPlan = !sub || sub.status === "none";
  const overCap = sub?.over_cap;
  const usagePct = sub?.usage_pct ?? 0;

  return (
    <div className="space-y-6">
      <SectionTitle title="Billing" subtitle="Prepaid wallet · plan · invoices · payment history." />

      {/* Over-cap upgrade prompt — only shown when buyer has exceeded their plan's leads_cap */}
      {overCap && sub?.suggested_upgrade && (
        <Card className="rounded-xl border-amber-300 bg-amber-50/40" data-testid="billing-over-cap-prompt">
          <CardContent className="p-5 flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-3 max-w-xl">
              <span className="inline-grid place-items-center size-10 rounded-lg bg-amber-100 text-amber-700 flex-shrink-0">
                <TrendingUp className="size-5" strokeWidth={1.6} />
              </span>
              <div>
                <div className="text-xs uppercase tracking-widest text-amber-700 font-semibold">Plan usage exceeded</div>
                <div className="text-[15px] font-medium text-slate-900 mt-1">You've routed {sub.accepted_this_period?.toLocaleString()} leads on a {sub.plan_label?.split(' —')[0]} plan ({sub.leads_cap?.toLocaleString()} cap).</div>
                <div className="text-[13px] text-slate-700 mt-1.5">
                  Lead delivery <span className="font-medium text-slate-900">continues uninterrupted</span> — but upgrading to <span className="font-medium text-slate-900">{sub.suggested_upgrade.label || sub.suggested_upgrade.id}</span> aligns your plan with actual volume and unlocks volume routing rebates.
                </div>
              </div>
            </div>
            {sub.suggested_upgrade.id !== "custom" ? (
              <Button onClick={() => subscribeTo(sub.suggested_upgrade.id)} disabled={busyPlan === sub.suggested_upgrade.id} className="rounded-full bg-amber-600 hover:bg-amber-700 text-white" data-testid="billing-over-cap-upgrade">
                {busyPlan === sub.suggested_upgrade.id ? <><Loader2 className="size-4 animate-spin" /> Redirecting…</> : `Upgrade to ${sub.suggested_upgrade.id} — £${sub.suggested_upgrade.amount}/mo`}
              </Button>
            ) : (
              <a href="/book-demo" className="inline-flex items-center justify-center rounded-full bg-amber-600 hover:bg-amber-700 text-white font-medium text-sm px-5 h-10" data-testid="billing-over-cap-custom">
                Talk to sales for custom quote →
              </a>
            )}
          </CardContent>
        </Card>
      )}

      {/* 80% soft prompt — early warning BEFORE hitting the cap */}
      {!overCap && usagePct >= 80 && sub?.leads_cap && sub?.suggested_upgrade && (
        <Card className="rounded-xl border-[#0055FF]/30 bg-[#0055FF]/[0.04]" data-testid="billing-soft-cap-prompt">
          <CardContent className="p-5 flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-3 max-w-xl">
              <span className="inline-grid place-items-center size-10 rounded-lg bg-[#0055FF]/[0.10] text-[#0055FF] flex-shrink-0">
                <TrendingUp className="size-5" strokeWidth={1.6} />
              </span>
              <div>
                <div className="text-xs uppercase tracking-[0.16em] text-[#0055FF] font-semibold">{Math.round(usagePct)}% of plan used</div>
                <div className="text-[15px] font-medium text-slate-900 mt-1">
                  {sub.accepted_this_period?.toLocaleString()} of {sub.leads_cap.toLocaleString()} leads consumed this period.
                </div>
                <div className="text-[13px] text-slate-600 mt-1.5">
                  At your current pace you'll hit your <span className="font-medium text-slate-900">{sub.plan_label?.split(' —')[0]}</span> plan cap soon. Upgrade now to <span className="font-medium text-slate-900">{sub.suggested_upgrade.label || sub.suggested_upgrade.id}</span> — same fixed CPL, larger headroom, locked rate.
                </div>
                <div className="mt-3 h-1.5 w-full max-w-[260px] rounded-full bg-slate-200 overflow-hidden">
                  <div className="h-full bg-[#0055FF] rounded-full transition-all" style={{ width: `${Math.min(100, usagePct)}%` }} />
                </div>
              </div>
            </div>
            {sub.suggested_upgrade.id !== "custom" ? (
              <Button onClick={() => subscribeTo(sub.suggested_upgrade.id)} disabled={busyPlan === sub.suggested_upgrade.id} className="rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="billing-soft-cap-upgrade">
                {busyPlan === sub.suggested_upgrade.id ? <><Loader2 className="size-4 animate-spin" /> Redirecting…</> : `Upgrade to ${sub.suggested_upgrade.id} — £${sub.suggested_upgrade.amount}/mo`}
              </Button>
            ) : (
              <a href="/book-demo" className="inline-flex items-center justify-center rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white font-medium text-sm px-5 h-10" data-testid="billing-soft-cap-custom">
                Get a volume quote →
              </a>
            )}
          </CardContent>
        </Card>
      )}

      {/* Plan card — full width, sits at the top */}
      <Card className="rounded-xl shadow-soft border-slate-200" data-testid="billing-plan-card">
        <CardContent className="p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <span className="inline-grid place-items-center size-10 rounded-lg bg-[#0F172A] text-white">
                <Sparkles className="size-5" strokeWidth={1.6} />
              </span>
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500">Platform plan</div>
                {noPlan ? (
                  <div className="text-base font-medium text-slate-900 mt-0.5">No active plan — pick one to unlock routing</div>
                ) : (
                  <>
                    <div className="text-base font-medium text-slate-900 mt-0.5" data-testid="billing-plan-label">{sub.plan_label || sub.plan_id}</div>
                    <div className="text-[12.5px] text-slate-500 mt-0.5">
                      {sub.status === "expired" ? (
                        <span className="inline-flex items-center gap-1.5 text-rose-600"><AlertTriangle className="size-3.5" /> Expired — renew to resume routing</span>
                      ) : sub.days_left !== null && sub.days_left !== undefined ? (
                        <span className="inline-flex items-center gap-1.5 text-emerald-700"><CheckCircle2 className="size-3.5" /> Renews in {sub.days_left} day{sub.days_left === 1 ? "" : "s"}</span>
                      ) : null}
                      {sub.leads_cap && <span className="ml-2 text-slate-500">· {sub.accepted_this_period?.toLocaleString() ?? 0} / {sub.leads_cap.toLocaleString()} leads this period</span>}
                    </div>
                    {/* Usage progress bar */}
                    {sub.leads_cap && (
                      <div className="mt-2 h-1.5 w-64 rounded-full bg-slate-100 overflow-hidden" data-testid="billing-usage-bar">
                        <div
                          className={`h-full transition-all ${overCap ? "bg-amber-500" : usagePct >= 80 ? "bg-amber-400" : "bg-[#0055FF]"}`}
                          style={{ width: `${Math.min(100, usagePct)}%` }}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            {needsRenewal && sub?.plan_id && (
              <Button onClick={() => subscribeTo(sub.plan_id)} disabled={busyPlan === sub.plan_id} className="rounded-full bg-[#0055FF] hover:bg-[#0044CC]" data-testid="billing-plan-renew">
                {busyPlan === sub.plan_id ? <><Loader2 className="size-4 animate-spin" /> Redirecting…</> : "Renew plan"}
              </Button>
            )}
          </div>

          {(noPlan || needsRenewal) && (sub?.all_plans?.length || 0) > 0 && (
            <div className="mt-5 grid sm:grid-cols-3 gap-3" data-testid="billing-plan-picker">
              {sub.all_plans.map((p) => (
                <button
                  key={p.id}
                  onClick={() => subscribeTo(p.id)}
                  disabled={busyPlan !== null}
                  className="text-left rounded-xl border border-slate-200 bg-white p-4 hover:border-[#0055FF] hover:bg-[#0055FF]/[0.03] transition-all disabled:opacity-50"
                  data-testid={`billing-plan-${p.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-slate-900 text-sm capitalize">{p.id}</div>
                    <div className="text-sm font-mono tabular-nums text-slate-900">£{p.amount.toFixed(0)}/mo</div>
                  </div>
                  <div className="text-[11.5px] text-slate-500 mt-1 leading-snug">Up to {p.leads_cap?.toLocaleString() || "—"} leads / month</div>
                  <div className="mt-3 text-[11.5px] text-[#0055FF] inline-flex items-center gap-1">
                    {busyPlan === p.id ? <><Loader2 className="size-3 animate-spin" /> Redirecting…</> : "Subscribe →"}
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Daily lead cap — buyer self-service inflow control */}
      <Card className="rounded-xl shadow-soft border-slate-200" data-testid="billing-daily-cap-card">
        <CardContent className="p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <span className="inline-grid place-items-center size-10 rounded-lg bg-slate-100 text-slate-700 flex-shrink-0">
                <Gauge className="size-5" strokeWidth={1.6} />
              </span>
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500">Daily lead cap</div>
                <div className="text-base font-medium text-slate-900 mt-0.5">
                  {prefs?.daily_lead_cap ? `${prefs.daily_lead_cap.toLocaleString()} leads / day` : "Unlimited"}
                </div>
                <div className="text-[12.5px] text-slate-500 mt-0.5">
                  {prefs?.today_accepted ?? 0} accepted today. Set 0 (or blank) to remove the cap.
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={0}
                max={100000}
                placeholder="No cap"
                value={capDraft}
                onChange={(e) => setCapDraft(e.target.value)}
                className="w-32 h-10"
                data-testid="billing-daily-cap-input"
              />
              <Button onClick={saveCap} disabled={savingCap} className="rounded-full h-10 px-5 bg-[#0F172A] hover:bg-slate-800" data-testid="billing-daily-cap-save">
                {savingCap ? <Loader2 className="size-4 animate-spin" /> : "Save"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Business hours — buyer self-service delivery window */}
      <DeliveryHoursCard
        initial={prefs?.delivery_hours}
        onSaved={refresh}
      />

      {/* Emergency pause delivery */}
      <Card className={`rounded-xl shadow-soft border-slate-200 ${paused ? "bg-amber-50/40" : ""}`} data-testid="billing-pause-delivery-card">
        <CardContent className="p-5 flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <span className={`inline-grid place-items-center size-10 rounded-lg flex-shrink-0 ${paused ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-700"}`}>
              <PauseCircle className="size-5" strokeWidth={1.6} />
            </span>
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500">Emergency pause</div>
              <div className="text-base font-medium text-slate-900 mt-0.5">{paused ? "All lead delivery is paused" : "Delivery is active"}</div>
              <div className="text-[12.5px] text-slate-500 mt-0.5">{paused ? "Publishers cannot push leads to your account. Wallet not debited." : "Hard-stops every inbound lead instantly. Toggle off to resume."}</div>
            </div>
          </div>
          <Switch checked={paused} onCheckedChange={togglePause} disabled={pausing} data-testid="billing-pause-toggle" />
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-1 min-w-0">
          <WalletCard />
        </div>
        <Card className="rounded-xl shadow-soft border-slate-200 lg:col-span-2 min-w-0">
          <CardContent className="p-0">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-medium text-slate-900">Invoices</h3>
              <span className="text-[11px] text-slate-500">All invoices include 20% VAT where applicable</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50/60"><tr className="text-xs uppercase tracking-widest text-slate-500 text-left">
                  <th className="py-3 px-4 font-medium">Number</th><th className="font-medium">Kind</th><th className="font-medium">Issued</th><th className="font-medium">Status</th><th className="font-medium text-right pr-4">Amount</th><th className="pr-4" />
                </tr></thead>
                <tbody>
                  {(invoices || []).length === 0 ? (
                    <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-400 italic">No invoices yet</td></tr>
                  ) : invoices.map((i) => (
                    <tr key={i.id} className="border-t border-slate-100 hover:bg-slate-50/40" data-testid={`bill-row-${(i.number || i.id || "").slice(0,8)}`}>
                      <td className="py-3 px-4 font-mono text-slate-900">{i.number || i.id?.slice(0, 8)}</td>
                      <td className="text-slate-600 text-[12.5px]">{(i.kind || "").replace(/_/g, " ") || "—"}</td>
                      <td className="text-slate-600">{i.issued_at ? new Date(i.issued_at).toLocaleDateString("en-GB") : "—"}</td>
                      <td><StatusPill status={i.status} /></td>
                      <td className="text-right pr-4 font-mono tabular-nums">£{Number(i.total_gbp ?? i.amount ?? 0).toFixed(2)}</td>
                      <td className="pr-4 text-right">
                        {i.id && (
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                const { data } = await api.get(`/invoices/${i.id}/share-link`);
                                if (data?.url) window.open(data.url, "_blank", "noopener");
                              } catch (e) {
                                toast.error(e?.response?.data?.detail || "Could not open PDF");
                              }
                            }}
                            className="text-[#0055FF] hover:underline text-[12.5px] inline-flex items-center gap-1"
                            data-testid={`invoice-pdf-${(i.number || i.id || "").slice(0,8)}`}
                          >
                            <Download className="size-3" /> PDF
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
